import React, { useState } from 'react';
import {
  Clock, Shirt, Car, Map as MapIcon, QrCode, Phone,
  Truck, ClipboardCheck, FileText, Bell, Pin, Plus, X,
  Sparkles, ChevronDown, ChevronUp,
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import api from '../utils/api';
import { toast } from 'sonner';

/**
 * Iter-177 — Channel pin tray + rich operational pinned cards.
 * Renders at the top of EventChannelDetail. Cards visually stand apart
 * from regular chat bubbles ("operations dashboard cards inside messaging").
 */

const KIND_META = {
  call_time:          { icon: Clock,           label: 'Call Time',         tone: 'cyan'    },
  uniform:            { icon: Shirt,           label: 'Uniform',           tone: 'violet'  },
  parking:            { icon: Car,             label: 'Parking',           tone: 'amber'   },
  venue_map:          { icon: MapIcon,         label: 'Venue Map',         tone: 'emerald' },
  qr_code:            { icon: QrCode,          label: 'QR Code',           tone: 'cyan'    },
  emergency_contact:  { icon: Phone,           label: 'Emergency Contact', tone: 'rose'    },
  load_in_out:        { icon: Truck,           label: 'Load In / Out',     tone: 'amber'   },
  check_in:           { icon: ClipboardCheck,  label: 'Check-In',          tone: 'emerald' },
  client_note:        { icon: FileText,        label: 'Client Note',       tone: 'slate'   },
  staffing_reminder:  { icon: Bell,            label: 'Staffing Reminder', tone: 'cyan'    },
  note:               { icon: Pin,             label: 'Note',              tone: 'slate'   },
};

const TONE_CLASSES = {
  cyan:    'border-cyan-200 bg-cyan-50/70 text-cyan-900',
  violet:  'border-violet-200 bg-violet-50/70 text-violet-900',
  amber:   'border-amber-200 bg-amber-50/70 text-amber-900',
  emerald: 'border-emerald-200 bg-emerald-50/70 text-emerald-900',
  rose:    'border-rose-300 bg-rose-50/80 text-rose-900',
  slate:   'border-slate-200 bg-white text-slate-900',
};

const PinCard = ({ pin, canEdit, onDelete }) => {
  const meta = KIND_META[pin.kind] || KIND_META.note;
  const Icon = meta.icon;
  const toneClass = TONE_CLASSES[meta.tone] || TONE_CLASSES.slate;
  const p = pin.payload || {};

  return (
    <div className={`min-w-[240px] max-w-[280px] shrink-0 rounded-xl border ${toneClass} p-3 relative`}
         data-testid={`pin-card-${pin.id}`}>
      <div className="flex items-start gap-2">
        <div className="w-8 h-8 rounded-lg bg-white/80 border border-current/10 flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-wide opacity-70">{meta.label}</p>
          <h4 className="font-semibold text-sm leading-tight truncate">{pin.title}</h4>
        </div>
        {canEdit && (
          <button onClick={() => onDelete(pin.id)} className="opacity-50 hover:opacity-100 transition" data-testid={`pin-delete-${pin.id}`}>
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {/* Card body — type-specific rendering */}
      <div className="mt-2 space-y-1 text-xs">
        {pin.kind === 'call_time' && (
          <>
            {p.time && <p><b>{p.time}</b></p>}
            {p.note && <p className="opacity-80">{p.note}</p>}
          </>
        )}
        {pin.kind === 'parking' && (
          <>
            {p.lot && <p>Lot: <b>{p.lot}</b></p>}
            {p.note && <p className="opacity-80">{p.note}</p>}
          </>
        )}
        {pin.kind === 'emergency_contact' && (
          <>
            {p.name && <p><b>{p.name}</b></p>}
            {p.phone && <a href={`tel:${p.phone}`} className="underline">{p.phone}</a>}
          </>
        )}
        {pin.kind === 'qr_code' && p.image_url && (
          <img src={p.image_url} alt="QR code" className="w-24 h-24 object-contain bg-white rounded" />
        )}
        {pin.kind === 'venue_map' && p.url && (
          <a href={p.url} target="_blank" rel="noreferrer" className="underline truncate block">Open map →</a>
        )}
        {(pin.kind === 'uniform' || pin.kind === 'load_in_out' || pin.kind === 'check_in' ||
          pin.kind === 'client_note' || pin.kind === 'staffing_reminder' || pin.kind === 'note') &&
          (p.body || p.note) && (
            <p className="opacity-90 line-clamp-3">{p.body || p.note}</p>
          )}
      </div>
    </div>
  );
};

const PinEditor = ({ open, onOpenChange, channelId, onSaved }) => {
  const [kind, setKind] = useState('call_time');
  const [title, setTitle] = useState('');
  const [time, setTime] = useState('');
  const [body, setBody] = useState('');
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [lot, setLot] = useState('');
  const [url, setUrl] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setKind('call_time'); setTitle(''); setTime(''); setBody('');
    setPhone(''); setName(''); setLot(''); setUrl(''); setImageUrl('');
  };

  const save = async () => {
    if (!title.trim()) {
      toast.error('Add a title');
      return;
    }
    setSaving(true);
    const payload = {};
    if (time) payload.time = time;
    if (body) payload.body = body;
    if (phone) payload.phone = phone;
    if (name) payload.name = name;
    if (lot) payload.lot = lot;
    if (url) payload.url = url;
    if (imageUrl) payload.image_url = imageUrl;
    try {
      await api.post(`/messages/channels/${channelId}/pins`, {
        kind, title: title.trim(), payload, position: 0,
      });
      toast.success('Pinned');
      reset();
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to pin');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-md" data-testid="pin-editor-dialog">
        <DialogHeader>
          <DialogTitle>Pin operational card</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Card type</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="pin-kind-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(KIND_META).map(([k, v]) => (
                  <SelectItem key={k} value={k} data-testid={`pin-kind-${k}`}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Title *</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Call time 7:30 AM" data-testid="pin-title-input" />
          </div>
          {kind === 'call_time' && (
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Time</Label><Input value={time} onChange={e => setTime(e.target.value)} placeholder="07:30 AM" /></div>
              <div><Label className="text-xs">Note</Label><Input value={body} onChange={e => setBody(e.target.value)} placeholder="Be 15 min early" /></div>
            </div>
          )}
          {kind === 'parking' && (
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Lot</Label><Input value={lot} onChange={e => setLot(e.target.value)} placeholder="Lot B" /></div>
              <div><Label className="text-xs">Note</Label><Input value={body} onChange={e => setBody(e.target.value)} placeholder="Free with badge" /></div>
            </div>
          )}
          {kind === 'emergency_contact' && (
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Name</Label><Input value={name} onChange={e => setName(e.target.value)} placeholder="Jane Doe" /></div>
              <div><Label className="text-xs">Phone</Label><Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 555…" /></div>
            </div>
          )}
          {kind === 'venue_map' && (
            <div><Label className="text-xs">Map URL</Label><Input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://goo.gl/maps/…" /></div>
          )}
          {kind === 'qr_code' && (
            <div><Label className="text-xs">QR image URL</Label><Input value={imageUrl} onChange={e => setImageUrl(e.target.value)} placeholder="https://…" /></div>
          )}
          {(kind === 'uniform' || kind === 'load_in_out' || kind === 'check_in' ||
            kind === 'client_note' || kind === 'staffing_reminder' || kind === 'note') && (
            <div><Label className="text-xs">Body</Label><Textarea rows={3} value={body} onChange={e => setBody(e.target.value)} placeholder="Operational details, instructions, reminders…" /></div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="pin-save-btn"
                  className="bg-cyan-500 hover:bg-cyan-600 text-white">
            Pin Card
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ChannelPinTray = ({ channelId, pinnedItems = [], canEdit = false, onChanged }) => {
  const [editorOpen, setEditorOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/messages/channels/${channelId}/pins/${id}`);
      toast.success('Pin removed');
      onChanged?.();
    } catch (e) {
      toast.error('Failed to remove pin');
    }
  };

  if (pinnedItems.length === 0 && !canEdit) return null;

  return (
    <div className="border-b border-gray-200 bg-gradient-to-b from-white to-gray-50" data-testid="channel-pin-tray">
      <div className="max-w-3xl mx-auto px-4 py-2">
        <div className="flex items-center justify-between mb-1.5">
          <button onClick={() => setCollapsed(c => !c)} className="flex items-center gap-1.5 text-xs font-semibold text-gray-700"
                  data-testid="pin-tray-toggle">
            <Pin className="w-3.5 h-3.5 text-cyan-500" />
            Operational Pins ({pinnedItems.length})
            {collapsed ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
          </button>
          {canEdit && (
            <Button size="sm" variant="ghost" onClick={() => setEditorOpen(true)}
                    className="h-7 text-cyan-700 hover:bg-cyan-50" data-testid="pin-add-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add
            </Button>
          )}
        </div>
        {!collapsed && (
          pinnedItems.length === 0 ? (
            <p className="text-xs text-gray-500 italic pb-2">No pinned cards yet — tap Add to share call times, parking, uniforms…</p>
          ) : (
            <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
              {pinnedItems.map(p => (
                <PinCard key={p.id} pin={p} canEdit={canEdit} onDelete={handleDelete} />
              ))}
            </div>
          )
        )}
      </div>
      <PinEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        channelId={channelId}
        onSaved={onChanged}
      />
    </div>
  );
};

export const CHANNEL_TYPE_META = {
  general:              { label: 'General',          tone: 'bg-cyan-100 text-cyan-800 border-cyan-200',     accent: 'border-cyan-200',    icon: Pin },
  staff:                { label: 'Staff',            tone: 'bg-slate-100 text-slate-700 border-slate-200',  accent: 'border-slate-200',   icon: Pin },
  client_announcements: { label: 'Announcements',    tone: 'bg-blue-600 text-white border-blue-700',        accent: 'border-blue-300',    icon: Bell },
  shift_lead:           { label: 'Shift Lead',       tone: 'bg-violet-100 text-violet-800 border-violet-200',accent: 'border-violet-200', icon: Pin },
  emergency:            { label: 'Emergency',        tone: 'bg-rose-600 text-white border-rose-700',        accent: 'border-rose-300',    icon: Phone },
  ops:                  { label: 'Ops',              tone: 'bg-amber-100 text-amber-800 border-amber-200',  accent: 'border-amber-200',   icon: ClipboardCheck },
  ai_assistant:         { label: 'Evie',     tone: 'bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white border-violet-400', accent: 'border-fuchsia-300', icon: Sparkles },
};

export default ChannelPinTray;
